/**
 * Product analytics for Grafty (frontend side).
 *
 * Pairs with the backend `internal/analytics` package — same event vocabulary,
 * same PostHog provider. Frontend captures user-side events; backend captures
 * server-side events. Both flow into the same PostHog project.
 *
 * USAGE:
 *
 *   import { useAnalytics } from '@/lib/analytics';
 *
 *   function HarvestForm() {
 *     const { track } = useAnalytics();
 *
 *     const onSubmit = async (data) => {
 *       await api.recordHarvest(data);
 *       track('harvest_recorded', { weight_kg: data.weight, variety: data.variety });
 *     };
 *   }
 *
 * KEY HANDLING:
 *   The PostHog public key is referrer-restricted (only *.grafty.io can use it),
 *   so it's safe to bake into the client bundle via VITE_POSTHOG_KEY.
 *   If unset, all calls become no-ops — fine for dev where you don't need
 *   analytics noise.
 *
 * PII:
 *   - distinctId is the user UUID (not email)
 *   - tenant_id always sent as a property for multi-tenant analysis
 *   - Never log raw user content (plant notes, harvest descriptions)
 *   - PostHog's session recording masks input fields by default — keep that on
 *
 * INSTALL POSTHOG SDK:
 *   When you actually wire up PostHog, add to package.json:
 *     npm install posthog-js
 *   Then uncomment the import below.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

// import posthog from 'posthog-js';  // ← uncomment after `npm install posthog-js`

// ── Stub PostHog client (used until SDK is installed) ─────────────────────────
//
// The shape mimics PostHog's actual API so swapping later requires no
// call-site changes — only this stub block changes.

interface PostHogLike {
  init(key: string, options: Record<string, unknown>): void;
  identify(distinctId: string, properties?: Record<string, unknown>): void;
  capture(event: string, properties?: Record<string, unknown>): void;
  group(groupType: string, groupKey: string, properties?: Record<string, unknown>): void;
  reset(): void;
}

const stubPostHog: PostHogLike = {
  init: () => {},
  identify: (id, props) => console.debug('[analytics:stub] identify', id, props),
  capture: (event, props) => console.debug('[analytics:stub] capture', event, props),
  group: (type, key, props) => console.debug('[analytics:stub] group', type, key, props),
  reset: () => console.debug('[analytics:stub] reset'),
};

// const realPostHog = posthog as unknown as PostHogLike;  // ← uncomment after install

// ── Analytics context ────────────────────────────────────────────────────────

export interface AnalyticsContextValue {
  /** Capture an event with optional properties. */
  track: (event: string, properties?: Record<string, unknown>) => void;
  /** Identify a user — call on login. */
  identify: (userId: string, properties?: Record<string, unknown>) => void;
  /** Group user with their tenant — call on login after identify. */
  group: (tenantId: string, properties?: Record<string, unknown>) => void;
  /** Reset on logout to avoid mixing identities. */
  reset: () => void;
  /** Is analytics actually enabled (real key configured)? */
  enabled: boolean;
}

const AnalyticsContext = createContext<AnalyticsContextValue>({
  track: () => {},
  identify: () => {},
  group: () => {},
  reset: () => {},
  enabled: false,
});

// ── Provider ─────────────────────────────────────────────────────────────────

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const enabled = Boolean(apiKey);

  // Pick the active PostHog client — stub when disabled.
  const client: PostHogLike = enabled ? stubPostHog /* ← swap to realPostHog after install */ : stubPostHog;

  useEffect(() => {
    if (!enabled || !apiKey) return;
    client.init(apiKey, {
      // Use posthog.com (cloud) by default; set VITE_POSTHOG_HOST to override
      // for self-hosted. Cloud is fine to start.
      api_host: (import.meta.env.VITE_POSTHOG_HOST as string) || 'https://app.posthog.com',
      // Privacy defaults: respect Do Not Track, mask password fields, redact
      // text input by default.
      respect_dnt: true,
      mask_all_text: false, // OK for now; toggle to true if PII concerns arise
      autocapture: true, // captures page views and basic clicks automatically
      // Session recording on free plan; turn off if it gets noisy.
      session_recording: {
        maskAllInputs: true,
      },
    });
  }, [enabled, apiKey, client]);

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      track: (event, props) => {
        if (!enabled) return;
        client.capture(event, props);
      },
      identify: (userId, props) => {
        if (!enabled) return;
        client.identify(userId, props);
      },
      group: (tenantId, props) => {
        if (!enabled) return;
        client.group('tenant', tenantId, props);
      },
      reset: () => {
        if (!enabled) return;
        client.reset();
      },
      enabled,
    }),
    [enabled, client],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useAnalytics(): AnalyticsContextValue {
  return useContext(AnalyticsContext);
}

// ── Standard event names ─────────────────────────────────────────────────────
// Keep these in sync with internal/analytics/analytics.go on the backend.

export const Events = {
  // Lifecycle
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  LOGOUT: 'logout',
  TENANT_PROVISIONED: 'tenant_provisioned',
  INVITATION_SENT: 'invitation_sent',
  INVITATION_ACCEPTED: 'invitation_accepted',

  // Domain
  HARVEST_RECORDED: 'harvest_recorded',
  PLANT_CREATED: 'plant_created',
  PLANT_UPDATED: 'plant_updated',
  ZONE_CREATED: 'zone_created',
  STICKER_ORDER_CREATED: 'sticker_order_created',
  STICKER_SCANNED: 'sticker_scanned',
  SURVEY_COMPLETED: 'survey_completed',
  IRRIGATION_STARTED: 'irrigation_started',
  REPORT_GENERATED: 'report_generated',
  VENDOR_CREATED: 'vendor_created',
  SALE_RECORDED: 'sale_recorded',

  // Navigation / engagement
  PAGE_VIEWED: '$pageview', // PostHog convention
  FEATURE_USED: 'feature_used',

  // Conversion
  PLAN_UPGRADED: 'plan_upgraded',
  PLAN_DOWNGRADED: 'plan_downgraded',
  CHECKOUT_STARTED: 'checkout_started',
  CHECKOUT_COMPLETED: 'checkout_completed',
} as const;

export type EventName = (typeof Events)[keyof typeof Events];
